import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Check, Eye, EyeOff, Pencil, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-white/15 py-2.5 font-mono2 text-sm text-[#f5f3ef] placeholder:text-[#64646e] placeholder:text-[11px] placeholder:tracking-[0.25em] focus:outline-none focus:border-[#38bdf8]/80 transition-colors";

export default function AdminLinks({ token }) {
  const [links, setLinks] = useState(null);
  const [form, setForm] = useState({ label: "", url: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", url: "" });
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const res = await fetch(`${API}/admin/links`, { headers });
    if (res.ok) setLinks(await res.json());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.url.trim()) return;
    await fetch(`${API}/admin/links`, { method: "POST", headers, body: JSON.stringify(form) });
    setForm({ label: "", url: "" });
    load();
  };

  const save = async (id) => {
    await fetch(`${API}/admin/links/${id}`, { method: "PATCH", headers, body: JSON.stringify(editForm) });
    setEditId(null);
    load();
  };

  const remove = async (id) => {
    await fetch(`${API}/admin/links/${id}`, { method: "DELETE", headers });
    load();
  };

  const toggle = async (l) => {
    await fetch(`${API}/admin/links/${l.id}`, { method: "PATCH", headers, body: JSON.stringify({ active: !l.active }) });
    load();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (!links || j < 0 || j >= links.length) return;
    await fetch(`${API}/admin/links/${links[i].id}`, { method: "PATCH", headers, body: JSON.stringify({ order: links[j].order }) });
    await fetch(`${API}/admin/links/${links[j].id}`, { method: "PATCH", headers, body: JSON.stringify({ order: links[i].order }) });
    load();
  };

  return (
    <div data-testid="admin-links" className="py-8">
      <form data-testid="admin-link-add-form" onSubmit={add} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-4 items-end pb-8 border-b border-white/10">
        <input
          data-testid="admin-link-label-input"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="LABEL (Z. B. SPOTIFY)"
          className={inputCls}
        />
        <input
          data-testid="admin-link-url-input"
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          placeholder="URL (HTTPS://…)"
          className={inputCls}
        />
        <button
          data-testid="admin-link-add-btn"
          type="submit"
          className="px-8 py-3 bg-[#f5f3ef] text-[#070709] font-display font-bold text-xs tracking-wide hover:bg-[#70c7fa] transition-all duration-300"
        >
          HINZUFÜGEN
        </button>
      </form>

      {links === null ? (
        <p className="py-14 font-mono2 text-xs tracking-[0.3em] text-[#64646e] uppercase">Lade Links…</p>
      ) : links.length === 0 ? (
        <p data-testid="admin-links-empty" className="py-14 font-mono2 text-xs tracking-[0.3em] text-[#64646e] uppercase">
          Keine Links vorhanden.
        </p>
      ) : (
        <div className="divide-y divide-white/5">
          {links.map((l, i) => (
            <div key={l.id} data-testid="admin-link-row" className={`py-4 flex items-center gap-3 ${l.active ? "" : "opacity-40"}`}>
              <div className="flex flex-col">
                <button data-testid="admin-link-up" onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-[#64646e] hover:text-[#70c7fa] disabled:opacity-20 transition-colors" aria-label="Hoch">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button data-testid="admin-link-down" onClick={() => move(i, 1)} disabled={i === links.length - 1} className="p-1 text-[#64646e] hover:text-[#70c7fa] disabled:opacity-20 transition-colors" aria-label="Runter">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>

              {editId === l.id ? (
                <div className="flex-1 grid sm:grid-cols-2 gap-3">
                  <input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} className={inputCls} />
                  <input value={editForm.url} onChange={(e) => setEditForm({ ...editForm, url: e.target.value })} className={inputCls} />
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-sm uppercase tracking-wide truncate">{l.label}</p>
                  <p className="font-mono2 text-[10px] text-[#64646e] truncate">{l.url}</p>
                </div>
              )}

              <div className="flex items-center gap-1">
                {editId === l.id ? (
                  <>
                    <button data-testid="admin-link-save" onClick={() => save(l.id)} className="p-2 text-[#38bdf8] hover:text-[#70c7fa] transition-colors" aria-label="Speichern">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditId(null)} className="p-2 text-[#64646e] hover:text-[#f5f3ef] transition-colors" aria-label="Abbrechen">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      data-testid="admin-link-edit"
                      onClick={() => {
                        setEditId(l.id);
                        setEditForm({ label: l.label, url: l.url });
                      }}
                      className="p-2 text-[#64646e] hover:text-[#70c7fa] transition-colors"
                      aria-label="Bearbeiten"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid="admin-link-toggle" onClick={() => toggle(l)} className="p-2 text-[#64646e] hover:text-[#70c7fa] transition-colors" aria-label="Aktiv umschalten">
                      {l.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button data-testid="admin-link-delete" onClick={() => remove(l.id)} className="p-2 text-[#64646e] hover:text-red-400 transition-colors" aria-label="Löschen">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-8 font-mono2 text-[10px] tracking-[0.25em] text-[#64646e] uppercase">
        Sichtbar unter tonesound.de/links
      </p>
    </div>
  );
}
